import React, { useEffect, useRef } from 'react';

import {
  DIFF_SHOWFOR,
  DiffShowFor_Keys,
} from '../RolloutDifference/RolloutDiffViewer/RolloutDiffUtils/RolloutDiffUtils';
import { Rollout, RolloutTarget, VariableInfo } from '@/lib/PlannerDTOs';

import './ChangelogTargetDiff.css';

import { Flex, Tooltip, Typography } from '@duxton-web/ui';
import { Empty } from 'antd';

import {
  extractInteger,
  getKeyFromValue,
  marks,
} from '../RolloutTab/RolloutEditor/RolloutTrackConf/RolloutTrackConf';
import ConstraintsViewer, {
  ConstraintsViewerType,
} from '@/common/components/ConstraintsViewer/ConstraintsViewer';
import { ChangelogState } from '@/common/const';
import { getRolloutTargetIndex } from '@/utils/rollout';

function RollouTargetWrapper(props: {
  rollout: Rollout;
  rolloutTarget: RolloutTarget;
  rolloutToCompare: Rollout;
  rolloutTargetToCompare: RolloutTarget;
  position: number;
  showFor: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys];
  changelog: any;
  variable: VariableInfo;
}) {
  const {
    rollout,
    rolloutTarget,
    rolloutToCompare,
    rolloutTargetToCompare,
    position,
    showFor = DIFF_SHOWFOR.ALL,
    changelog,
    variable,
  } = props;

  if (!rolloutTarget && !rolloutTargetToCompare) {
    const rolloutLimit = rollout?.trackConf?.limit;

    if (!rolloutLimit)
      return (
        <Flex vertical gap={8}>
          <Flex
            align="center"
            gap={4}
            className={`rollout-target-value-${
              showFor === DIFF_SHOWFOR.BEFORE ? 'deleted' : 'added'
            }`}
          >
            &nbsp;
          </Flex>
        </Flex>
      );

    const sampleRatePercentage = rolloutLimit
      ? Math.round((1 - rolloutLimit) * 100)
      : 100;
    const toDisplay = extractInteger(
      marks[getKeyFromValue(marks, sampleRatePercentage)!],
    );

    return (
      <Flex vertical gap={8}>
        <Flex
          align="center"
          gap={4}
          className={`rollout-target-value-${
            showFor === DIFF_SHOWFOR.BEFORE ? 'deleted' : 'added'
          }`}
        >
          <Typography.Text strong>%</Typography.Text>
          <Tooltip title={toDisplay}>
            <Typography.Text>{toDisplay}</Typography.Text>
          </Tooltip>
        </Flex>
      </Flex>
    );
  }

  if (!rolloutTarget)
    return (
      <Flex align="center" justify="center" style={{ height: '100%' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Flex>
    );

  return (
    <ConstraintsViewer
      rollout={rollout}
      rolloutTarget={rolloutTarget}
      rolloutToCompare={rolloutToCompare}
      rolloutTargetToCompare={rolloutTargetToCompare}
      showFor={showFor}
      variableType={changelog?.type || variable?.type}
      domain={changelog?.domain || variable?.domain}
      position={position}
      type={ConstraintsViewerType.ROLLOUT}
    />
  );
}

function ChangelogTargetDiffWrapper({ value, changelog, variable }) {
  function getPreviousData() {
    return {
      rollout: value?.before?.rollout || null,
      rolloutTarget: value?.before?.rolloutTarget || null,
      index: value?.before?.index || -1,
    };
  }

  function getCurrentData() {
    return {
      rollout: value?.after?.rollout || null,
      rolloutTarget: value?.after?.rolloutTarget || null,
      index: value?.after?.index || -1,
    };
  }

  const { rollout: previousRollout, rolloutTarget: previousRolloutTarget } =
    getPreviousData();
  const { rollout: currentRollout, rolloutTarget: currentRolloutTarget } =
    getCurrentData();

  if (value.state === ChangelogState.NONE)
    return (
      <div
        data-testid="changelog-item-unchanged-rollout-target-container"
        className="changelog-item-unchanged-rollout-target-container"
        style={{ width: '100%' }}
      >
        <RollouTargetWrapper
          rollout={previousRollout}
          rolloutTarget={previousRolloutTarget}
          rolloutToCompare={currentRollout}
          rolloutTargetToCompare={currentRolloutTarget}
          position={getRolloutTargetIndex(
            currentRolloutTarget,
            currentRollout?.rollouts,
          )}
          showFor={DIFF_SHOWFOR.ALL}
          changelog={changelog}
          variable={variable}
        />
      </div>
    );

  return (
    <>
      <div className="changelog-item-rollout-targets-diff-wrapper">
        <div className="changelog-item-before-rollout-targets-diff-title">
          BEFORE
        </div>
        <div
          data-testid="changelog-item-before-rollout-target-container"
          className="changelog-item-before-rollout-target-container"
        >
          <RollouTargetWrapper
            rollout={previousRollout}
            rolloutTarget={previousRolloutTarget}
            rolloutToCompare={currentRollout}
            rolloutTargetToCompare={currentRolloutTarget}
            position={getRolloutTargetIndex(
              previousRolloutTarget,
              previousRollout?.rollouts,
            )}
            showFor={DIFF_SHOWFOR.BEFORE}
            changelog={changelog}
            variable={variable}
          />
        </div>
      </div>

      <div className="changelog-item-rollout-targets-diff-wrapper">
        <div className="changelog-item-after-rollout-targets-diff-title">
          AFTER
        </div>
        <div
          data-testid="changelog-item-after-rollout-target-container"
          className="changelog-item-after-rollout-target-container"
        >
          <RollouTargetWrapper
            rollout={currentRollout}
            rolloutTarget={currentRolloutTarget}
            rolloutToCompare={previousRollout}
            rolloutTargetToCompare={previousRolloutTarget}
            position={getRolloutTargetIndex(
              currentRolloutTarget,
              currentRollout?.rollouts,
            )}
            showFor={DIFF_SHOWFOR.AFTER}
            changelog={changelog}
            variable={variable}
          />
        </div>
      </div>
    </>
  );
}

export default function ChangelogTargetDiff({ value, changelog, variable }) {
  const changelogTargetDiffRef = useRef(null);

  const resizeObserver = new ResizeObserver((entries) => {
    if (!changelogTargetDiffRef.current) return;
    if (entries?.[0].target === changelogTargetDiffRef.current) {
      const parentHeight = changelogTargetDiffRef?.current?.offsetHeight;

      changelogTargetDiffRef.current
        .getElementsByClassName(
          'changelog-item-before-rollout-target-container',
        )?.[0]
        ?.setAttribute(
          'style',
          `height: calc(${parentHeight}px + 32px); max-height: 460px;`,
        );
      changelogTargetDiffRef.current
        .getElementsByClassName(
          'changelog-item-after-rollout-target-container',
        )?.[0]
        ?.setAttribute(
          'style',
          `height: calc(${parentHeight}px + 32px); max-height: 460px;`,
        );

      resizeObserver.disconnect();
    }
  });

  useEffect(() => {
    resizeObserver.observe(changelogTargetDiffRef.current);
  }, []);

  return (
    <Flex
      align="flex-start"
      justify="space-between"
      ref={changelogTargetDiffRef}
    >
      <ChangelogTargetDiffWrapper
        value={value}
        changelog={changelog}
        variable={variable}
      />
    </Flex>
  );
}
